import type { Model3DElement, Project } from '../types';

export type FurnitureModelFormat = 'obj' | 'glb' | 'gltf' | 'fbx';

export interface SavedModelAsset {
  hash: string;
  format: FurnitureModelFormat;
  mimeType: string;
  data: string;
  sourcePath: string;
}

export interface SavedProjectAssets {
  version: 1;
  models: Record<string, SavedModelAsset>;
}

export type ModelSourceReader = (sourcePath: string) => Promise<ArrayBuffer>;

const FORMAT_MIME_TYPES: Record<FurnitureModelFormat, string> = {
  obj: 'model/obj',
  glb: 'model/gltf-binary',
  gltf: 'model/gltf+json',
  fbx: 'application/octet-stream',
};

const MIME_FORMATS: Record<string, FurnitureModelFormat> = {
  'model/obj': 'obj',
  'model/gltf-binary': 'glb',
  'model/gltf+json': 'gltf',
  'application/octet-stream': 'fbx',
};

const INTERNAL_PREFIX = 'kimap-asset://';
const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const HASH_PATTERN = /^[a-f0-9]{64}$/;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(data: string): Uint8Array {
  if (!data || !BASE64_PATTERN.test(data)) {
    throw new Error('Invalid or empty Base64 model data');
  }

  let binary: string;
  try {
    binary = atob(data);
  } catch {
    throw new Error('Invalid Base64 model data');
  }

  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytes.length === 0 || bytesToBase64(bytes) !== data) {
    throw new Error('Invalid or empty Base64 model data');
  }
  return bytes;
}

function formatAndMime(sourcePath: string): { format: FurnitureModelFormat; mimeType: string } {
  if (sourcePath.startsWith('data:')) {
    const match = /^data:([^;,]+)(?:;[^,]*)?,/i.exec(sourcePath);
    const mimeType = match?.[1]?.toLowerCase();
    const format = mimeType ? MIME_FORMATS[mimeType] : undefined;
    if (!format) throw new Error(`Unsupported model data MIME type: ${mimeType ?? 'missing'}`);
    return { format, mimeType: FORMAT_MIME_TYPES[format] };
  }

  const cleanPath = sourcePath.split(/[?#]/, 1)[0];
  const extension = cleanPath.slice(cleanPath.lastIndexOf('.') + 1).toLowerCase();
  if (!['obj', 'glb', 'gltf', 'fbx'].includes(extension)) {
    throw new Error(`Unsupported furniture model format: ${sourcePath}`);
  }
  const format = extension as FurnitureModelFormat;
  return { format, mimeType: FORMAT_MIME_TYPES[format] };
}

function dataUrlBytes(sourcePath: string): Uint8Array {
  const match = /^data:([^,]*),(.*)$/is.exec(sourcePath);
  if (!match) throw new Error('Invalid data model URL');
  const [, metadata, payload] = match;
  return /;base64(?:;|$)/i.test(metadata)
    ? base64ToBytes(payload)
    : new TextEncoder().encode(decodeURIComponent(payload));
}

function resolveGltfResourcePath(sourcePath: string, resourceUri: string): string {
  if (/^(?:data:|blob:|https?:)/i.test(resourceUri)) return resourceUri;
  if (/^(?:data:|blob:)/i.test(sourcePath)) {
    throw new Error(`Cannot resolve relative GLTF resource "${resourceUri}" from a data URL`);
  }
  if (/^https?:/i.test(sourcePath)) return new URL(resourceUri, sourcePath).toString();

  const sourceIsRootRelative = sourcePath.startsWith('/');
  const resolved = new URL(resourceUri, `https://kimap.local/${sourcePath.replace(/^\//, '')}`);
  const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  return sourceIsRootRelative ? path : path.replace(/^\//, '');
}

function imageMimeType(resourceUri: string): string {
  const cleanPath = resourceUri.split(/[?#]/, 1)[0];
  const extension = cleanPath.slice(cleanPath.lastIndexOf('.') + 1).toLowerCase();
  const mimeTypes: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    avif: 'image/avif',
    ktx2: 'image/ktx2',
  };
  const mimeType = mimeTypes[extension];
  if (!mimeType) throw new Error(`Unsupported external GLTF image format: ${resourceUri}`);
  return mimeType;
}

async function inlineGltfResources(
  sourceBytes: Uint8Array,
  sourcePath: string,
  sourceReader: ModelSourceReader,
): Promise<Uint8Array> {
  let gltf: {
    buffers?: Array<{ uri?: string }>;
    images?: Array<{ uri?: string; bufferView?: number }>;
  };
  try {
    gltf = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(sourceBytes));
  } catch {
    throw new Error('Invalid GLTF JSON');
  }

  let changed = false;
  for (const buffer of gltf.buffers ?? []) {
    if (typeof buffer.uri !== 'string') {
      throw new Error('GLTF buffer is missing its URI');
    }
    if (buffer.uri.startsWith('data:')) continue;
    const resourcePath = resolveGltfResourcePath(sourcePath, buffer.uri);
    const resourceBytes = new Uint8Array(await sourceReader(resourcePath));
    if (resourceBytes.length === 0) throw new Error(`GLTF resource has empty bytes: ${resourcePath}`);
    buffer.uri = `data:application/octet-stream;base64,${bytesToBase64(resourceBytes)}`;
    changed = true;
  }

  for (const image of gltf.images ?? []) {
    if (typeof image.uri !== 'string' || image.uri.startsWith('data:')) continue;
    const resourcePath = resolveGltfResourcePath(sourcePath, image.uri);
    const resourceBytes = new Uint8Array(await sourceReader(resourcePath));
    if (resourceBytes.length === 0) throw new Error(`GLTF resource has empty bytes: ${resourcePath}`);
    image.uri = `data:${imageMimeType(image.uri)};base64,${bytesToBase64(resourceBytes)}`;
    changed = true;
  }

  return changed ? new TextEncoder().encode(JSON.stringify(gltf)) : sourceBytes;
}

export async function readModelSource(sourcePath: string): Promise<ArrayBuffer> {
  if (sourcePath.startsWith('data:')) {
    const data = dataUrlBytes(sourcePath);
    const copy = new Uint8Array(data.byteLength);
    copy.set(data);
    return copy.buffer;
  }
  const response = await fetch(sourcePath);
  if (!response.ok) throw new Error(`Unable to read model source (${response.status}): ${sourcePath}`);
  return response.arrayBuffer();
}

function modelElements(project: Project): Model3DElement[] {
  return project.floors.flatMap((floor) => floor.layers.flatMap((layer) =>
    layer.elements.filter((element): element is Model3DElement => element.type === 'model3d'),
  ));
}

function assetError(element: Model3DElement, sourcePath: string, cause: unknown): Error {
  const name = element.modelName || element.id;
  const detail = cause instanceof Error ? `: ${cause.message}` : '';
  return new Error(`Unable to pack furniture model "${name}" from "${sourcePath}"${detail}`);
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const input = new Uint8Array(bytes.byteLength);
  input.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', input.buffer);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

export async function packProjectFurnitureAssets(
  project: Project,
  sourceReader: ModelSourceReader = readModelSource,
): Promise<{ project: Project; assets: SavedProjectAssets }> {
  const packedProject = structuredClone(project);
  const assets: SavedProjectAssets = { version: 1, models: {} };

  for (const element of modelElements(packedProject)) {
    const sourcePath = element.modelPath;
    try {
      const { format, mimeType } = formatAndMime(sourcePath);
      let bytes: Uint8Array = new Uint8Array(await sourceReader(sourcePath));
      if (bytes.length === 0) throw new Error('Furniture model source has empty bytes');
      if (format === 'gltf') bytes = await inlineGltfResources(bytes, sourcePath, sourceReader);
      const hash = await sha256(bytes);
      if (!assets.models[hash]) {
        assets.models[hash] = { hash, format, mimeType, data: bytesToBase64(bytes), sourcePath };
      }
      element.modelPath = `${INTERNAL_PREFIX}${hash}`;
    } catch (error) {
      throw assetError(element, sourcePath, error);
    }
  }

  return { project: packedProject, assets };
}

function validateAsset(hash: string, asset: SavedModelAsset): void {
  if (!HASH_PATTERN.test(hash) || asset.hash !== hash) {
    throw new Error(`Invalid furniture asset hash: ${hash}`);
  }
  if (FORMAT_MIME_TYPES[asset.format] !== asset.mimeType) {
    throw new Error(`Unsupported furniture asset format or MIME type for ${hash}`);
  }
  base64ToBytes(asset.data);
}

export function restoreProjectFurnitureAssets(project: Project, assets?: SavedProjectAssets): Project {
  const restoredProject = structuredClone(project);
  if (!assets) return restoredProject;
  if (assets.version !== 1) throw new Error('Unsupported saved furniture asset version');
  if (!assets.models || typeof assets.models !== 'object' || Array.isArray(assets.models)) {
    throw new Error('Invalid saved furniture asset models');
  }

  for (const [hash, asset] of Object.entries(assets.models)) validateAsset(hash, asset);

  for (const element of modelElements(restoredProject)) {
    if (!element.modelPath.startsWith(INTERNAL_PREFIX)) continue;
    const hash = element.modelPath.slice(INTERNAL_PREFIX.length);
    const asset = assets.models[hash];
    if (!asset) throw new Error(`Missing furniture asset: ${hash}`);
    element.modelPath = `data:${asset.mimeType};base64,${asset.data}`;
  }

  return restoredProject;
}
